import { inject } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivateFn, Router, RouterStateSnapshot, UrlTree } from '@angular/router';
import { RoleAccessService } from '../../shared/services/role-access.service';
import { RoutePath } from '../../shared/enums/common.enum';
import { PermissionAction } from '../../shared/constants/role.constants';

const normalizeRequiredRoles = (route: ActivatedRouteSnapshot): string[] => {
  const data = route.data || {};
  const required = data['requiredRoles'] ?? data['requiredRole'] ?? data['role'] ?? [];
  if (Array.isArray(required)) return required;
  if (typeof required === 'string') return [required];
  return [];
};

const normalizeRequiredActions = (route: ActivatedRouteSnapshot): PermissionAction[] => {
  const data = route.data || {};
  const required = data['requiredActions'] ?? data['requiredAction'] ?? [];
  if (Array.isArray(required)) return required as PermissionAction[];
  if (typeof required === 'string') return [required as PermissionAction];
  return [];
};

/**
 * Role Guard - restricts sub-admin routes to delegated roles
 */
export const roleGuard: CanActivateFn = (
  route: ActivatedRouteSnapshot,
  state: RouterStateSnapshot
): boolean | UrlTree => {
  const router = inject(Router);
  const roleAccessService = inject(RoleAccessService);

  if (!roleAccessService.isSubAdmin()) {
    return true;
  }

  const activeRoles = roleAccessService.getActiveRoleIds();
  const requestedUrl = state.url || '';
  const isDashboard = requestedUrl.startsWith(`/${RoutePath.DASHBOARD}`);

  if (activeRoles.length === 0 && !isDashboard) {
    return router.createUrlTree([`/${RoutePath.DASHBOARD}`]);
  }

  const requiredRoles = normalizeRequiredRoles(route);
  const requiredActions = normalizeRequiredActions(route);
  if (requiredRoles.length === 0) {
    return true;
  }

  const requiredCategoryId = route.data?.['requiredCategoryId'] ?? route.data?.['categoryId'];

  const hasAccess = requiredRoles.some(role => {
    if (!roleAccessService.hasRole(role)) return false;

    if (requiredCategoryId && !roleAccessService.canAccessCategory(role, requiredCategoryId)) {
      return false;
    }

    if (requiredActions.length > 0) {
      return requiredActions.every(action => roleAccessService.canPerform(role, action, requiredCategoryId));
    }

    return true;
  });

  if (!hasAccess && !isDashboard) {
    return router.createUrlTree([`/${RoutePath.DASHBOARD}`]);
  }

  return hasAccess || isDashboard;
};
